import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'
import { BackButton, FullPageSpinner } from 'components'
import { Redirect, useParams, useHistory } from 'react-router-dom'
import { Path, TaxableAccountsActionItems } from 'config/portal'
import { TOAST_ERROR_CONTACT_SUPPORT } from 'config/notifications'
import { ServicesAgreementStatus } from 'config/taxable-accounts'
import { flashErrorMessage } from 'redux-flash'
import * as actions from '../actions'
import { TabBar } from 'lp-components'
import TaxableAccountsServicesAgreementParticipants from './TaxableAccountsServicesAgreementParticipants'
import { displayEin } from 'utils'
import { CombinedServiceAgreementStatus } from 'config/automatic-rollovers'
import { selectors as globalSelectors } from '../../reducer'
import { downloadServicesAgreementFile } from 'api'
import {
  AgreementDetailsActions,
  AgreementDetailsModals,
  AgreementDetailsTabContent,
} from '../components'
import { ProductAgreementType } from 'config/products'
import participantFieldMappings from '../components/participantFieldMapping'

const Tab = {
  AGREEMENT: 'agreement',
  PARTICIPANTS: 'participants',
}

const propTypes = {
  fetchServicesAgreementDetailsTaxableAccounts: PropTypes.func.isRequired,
  servicesAgreementTaxableAccounts: Types.servicesAgreementShow,
  flashErrorMessageHandler: PropTypes.func.isRequired,
  clearServicesAgreementDetails: PropTypes.func.isRequired,
  shouldTriggerServicesAgreementDetailsFetch: PropTypes.bool.isRequired,
  setShouldTriggerServicesAgreementDetailsFetch: PropTypes.func.isRequired,
  setShouldTriggerAllTaxableServicesAgreementsFetch: PropTypes.func.isRequired,
  currentUser: Types.user.isRequired,
}

const defaultProps = {
  servicesAgreementTaxableAccounts: null,
}
const PARTICIPANTS_INITIAL_FILTERS = {
  status: [],
}

function NoDetailsProvidedCard({ sectionName }) {
  return (
    <div className="card no-details">
      <h2 className="section-header">{sectionName} Details</h2>
      <p className="not-provided">Not provided</p>
    </div>
  )
}
NoDetailsProvidedCard.propTypes = {
  sectionName: PropTypes.string.isRequired,
}

function TaxableAccountsAgreementDetails({
  fetchServicesAgreementDetailsTaxableAccounts,
  servicesAgreementTaxableAccounts,
  flashErrorMessageHandler,
  clearServicesAgreementDetails,
  shouldTriggerServicesAgreementDetailsFetch,
  setShouldTriggerServicesAgreementDetailsFetch,
  setShouldTriggerAllTaxableServicesAgreementsFetch,
  currentUser,
}) {
  const { servicesAgreementId } = useParams()
  const [errorExists, setErrorExists] = useState(false)
  const [showDownloadNotAvailableModal, setShowDownloadNotAvailableModal] =
    useState(false)
  const [showResendModal, setShowResendModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [currentTab, setCurrentTab] = useState(Tab.AGREEMENT)
  const [participantsFilters, setParticipantsFilters] = useState(
    PARTICIPANTS_INITIAL_FILTERS
  )
  const [participantsSearch, setParticipantsSearch] = useState('')
  const history = useHistory()

  useEffect(() => {
    return () => {
      clearServicesAgreementDetails()
      setShouldTriggerServicesAgreementDetailsFetch(true)
    }
  }, [])

  useEffect(() => {
    async function getPlanAgreementDetails() {
      try {
        await fetchServicesAgreementDetailsTaxableAccounts(servicesAgreementId)
        setShouldTriggerServicesAgreementDetailsFetch(false)
      } catch {
        setErrorExists(true)
      }
    }
    if (shouldTriggerServicesAgreementDetailsFetch) getPlanAgreementDetails()
  }, [shouldTriggerServicesAgreementDetailsFetch])

  const isLoading = !servicesAgreementTaxableAccounts && !errorExists

  if (isLoading) return <FullPageSpinner />
  if (errorExists) {
    flashErrorMessageHandler(TOAST_ERROR_CONTACT_SUPPORT)
    return <Redirect to={Path.TAXABLE_ACCOUNTS_SERVICES_AGREEMENTS} />
  }

  const {
    name,
    portalAgreementStatus,
    createdDate,
    planEIN,
    planNumber,
    portalAgreementType,
    planType,
    planStatus,
    providerPlanId,
    planSponsorCompany,
    planSponsorContact,
    consultantCompany,
    consultantContact,
    TPACompany,
    TPAContact,
    recordKeeperCompany,
    recordKeeperContact,
    signingMethod,
  } = servicesAgreementTaxableAccounts

  const handleDownloadSelect = async () => {
    try {
      await downloadServicesAgreementFile(
        servicesAgreementId,
        name,
        ProductAgreementType.TAXABLE_ACCOUNTS
      )
    } catch (e) {
      if (e.status === 422) setShowDownloadNotAvailableModal(true)
      else setErrorExists(true)
    }
  }

  const displayPlanEinValue = displayEin(planEIN)

  const agreementAudience = currentUser.accountAudience

  const taxableAccountsActionItems = [
    {
      externalLink: process.env.REACT_APP_ARO_FUNDING_INSTRUCTIONS_URL,
      text: TaxableAccountsActionItems.VIEW_FUNDING_INSTRUCTIONS,
    },
    {
      handleSelect: () => handleDownloadSelect(),
      text: 'Download completed agreement',
    },
  ]

  return (
    <div className="services-agreement-details-view">
      <div className="header-bar">
        <BackButton
          link={(location) => ({
            pathname: Path.TAXABLE_ACCOUNTS_SERVICES_AGREEMENTS,
            search: location.state?.search,
          })}
        >
          Back to all agreements
        </BackButton>
        <div className="header-bar-content">
          <div className="header-bar-page-title">
            <h1 className="page-title">{name}</h1>
          </div>
          <AgreementDetailsActions
            portalAgreementStatus={portalAgreementStatus}
            signingMethod={signingMethod}
            actionItems={taxableAccountsActionItems}
            actionMenuLabel="View taxable account action items"
            onInitiate={() => history.push('/taxable-accounts/upload-file')}
            onUploadSignedAgreement={() => setShowUploadModal(true)}
            onResendDocusignEmail={() => setShowResendModal(true)}
            onDownloadAgreement={handleDownloadSelect}
            initiateLabel={TaxableAccountsActionItems.OPEN_TAXABLE_ACCOUNT}
            uploadSignedAgreementLabel={
              TaxableAccountsActionItems.UPLOAD_SIGNED_AGREEMENT
            }
            resendDocusignEmailLabel={
              TaxableAccountsActionItems.RESEND_DOCUSIGN_EMAIL
            }
            downloadAgreementLabel={
              TaxableAccountsActionItems.DOWNLOAD_AGREEMENT
            }
          />
        </div>
      </div>
      {(portalAgreementStatus === ServicesAgreementStatus.COMPLETED ||
        portalAgreementStatus ===
          CombinedServiceAgreementStatus.COMBINED_COMPLETED) && (
        <TabBar
          options={[
            { key: 'Agreement Details', value: Tab.AGREEMENT },
            { key: 'Participants', value: Tab.PARTICIPANTS },
          ]}
          value={currentTab}
          onChange={setCurrentTab}
        />
      )}
      {currentTab === Tab.AGREEMENT && (
        <AgreementDetailsTabContent
          isLoading={shouldTriggerServicesAgreementDetailsFetch}
          name={name}
          portalAgreementStatus={portalAgreementStatus}
          createdDate={createdDate}
          planNumber={planNumber}
          providerPlanId={providerPlanId}
          portalAgreementType={portalAgreementType}
          planType={planType}
          planStatus={planStatus}
          agreementAudience={agreementAudience}
          planSponsorCompany={planSponsorCompany}
          planSponsorContact={planSponsorContact}
          displayPlanEinValue={displayPlanEinValue}
          TPACompany={TPACompany}
          TPAContact={TPAContact}
          consultantCompany={consultantCompany}
          consultantContact={consultantContact}
          recordKeeperCompany={recordKeeperCompany}
          recordKeeperContact={recordKeeperContact}
          NoDetailsProvidedCard={NoDetailsProvidedCard}
        />
      )}
      {currentTab === Tab.PARTICIPANTS && (
        <TaxableAccountsServicesAgreementParticipants
          servicesAgreementId={servicesAgreementId}
          baseFilters={PARTICIPANTS_INITIAL_FILTERS}
          filters={participantsFilters}
          handleFilter={(filters) => setParticipantsFilters(filters)}
          search={participantsSearch}
          handleSearch={(q) => setParticipantsSearch(q)}
          fieldMappings={participantFieldMappings}
        />
      )}
      <AgreementDetailsModals
        showUploadModal={showUploadModal}
        setShowUploadModal={setShowUploadModal}
        showDownloadNotAvailableModal={showDownloadNotAvailableModal}
        setShowDownloadNotAvailableModal={setShowDownloadNotAvailableModal}
        showResendModal={showResendModal}
        setShowResendModal={setShowResendModal}
        servicesAgreement={servicesAgreementTaxableAccounts}
        servicesAgreementId={servicesAgreementId}
        portalAgreementStatus={portalAgreementStatus}
        agreementsType={ProductAgreementType.TAXABLE_ACCOUNTS}
        onUploadSuccess={() => {
          setShowUploadModal(false)
          setShowUploadModal(false)
          setShouldTriggerServicesAgreementDetailsFetch(true)
          setShouldTriggerAllTaxableServicesAgreementsFetch(true)
        }}
      />
    </div>
  )
}

TaxableAccountsAgreementDetails.propTypes = propTypes
TaxableAccountsAgreementDetails.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    servicesAgreementTaxableAccounts:
      selectors.servicesAgreementTaxableAccounts(state),
    shouldTriggerServicesAgreementDetailsFetch:
      selectors.shouldTriggerServicesAgreementDetailsFetch(state),
    currentUser: globalSelectors.currentUser(state),
  }
}

const mapDispatchToProps = {
  fetchServicesAgreementDetailsTaxableAccounts:
    apiActions.fetchServicesAgreementDetailsTaxableAccounts,
  flashErrorMessageHandler: flashErrorMessage,
  clearServicesAgreementDetails: actions.clearServicesAgreementDetails,
  setShouldTriggerServicesAgreementDetailsFetch:
    actions.setShouldTriggerServicesAgreementDetailsFetch,
  setShouldTriggerAllTaxableServicesAgreementsFetch:
    actions.setShouldTriggerAllTaxableServicesAgreementsFetch,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  TaxableAccountsAgreementDetails
)
